/* eslint-disable no-console */
import { AnimatePresence, motion } from 'framer-motion';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FiX } from 'react-icons/fi';
import { Step } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { Maybe } from 'yup';

import { useBalance } from '../../hooks/useBalance';
import { PLAYERSFIELD_ID_KEYS_BY_ABBREVIATION } from '../../utils/constants';

import { ChangePlayer } from './components/ChangePlayer';
import { Layout } from './components/Layout';
import { useIsBeforeVeda } from './hooks/useIsBeforeVeda';

import { Loader, Modal, NavBar } from '@/components';
import { Alert } from '@/components/Alert';
import { BoxGoPremium } from '@/components/BoxGoPremium';
import { FieldAssistant } from '@/components/FieldAssistant';
import { AdSlot } from '@/components/GoogleAds/AdSlot';
import { ChangeOptions, FieldLayout } from '@/components/Layouts/FieldLayout11';
import { OnboardingStepper } from '@/components/OnboardingStepper';
import { PinsDiscountLegend } from '@/components/PinsDiscountLegend';
import { DefaultTabsProps, Tabs } from '@/components/Tabs';
import { BoxNewsTeam } from '@/components/Team/BoxNewsTeam';
import { BoxRankingTeam } from '@/components/Team/BoxRankingTeam';
import { BoxTshirtEdit } from '@/components/Team/BoxTshirtEdit';
import {
  DetailsPlayer,
  DetailsPlayerSubmit,
} from '@/components/Team/DetailsPlayer';
import { FormFilterPlayers } from '@/components/Team/FormFilterPlayers';
import { HeaderMyTeam } from '@/components/Team/HeaderMyTeam';
import { ModalChangeConfirm } from '@/components/Team/ModalChangeConfirm';
import { ModalChangeSuggestion } from '@/components/Team/ModalChangeSuggestion';
import { ModalWhatsPins } from '@/components/Team/ModalPinsF11';
import { DesktopSidebar, MobileSidebar } from '@/components/Team/Sidebar';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import useCheckPublicacion from '@/hooks/useCheckPublicacion';
import { useGoogleAds } from '@/hooks/useGoogleAds';
import { useHandleChangeCaptain } from '@/hooks/useHandleChangeCaptain';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useHandleTransfer } from '@/hooks/useHandleTransfer';
import useHostbackButton, {
  useUndepsHostbackButton,
} from '@/hooks/useHostbackButton';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMetaData } from '@/hooks/useMetaData';
import { useGetCurrentTeamF11, usePostDataRanking } from '@/hooks/useMyTeam11';
import usePinsAccount from '@/hooks/usePinsAccount';
import { usePlayerClub } from '@/hooks/usePlayer';
import { useTeam } from '@/hooks/useTeam11';
import { useTeamStore } from '@/hooks/useTeamStore';
import useToast from '@/hooks/useToast';
import { usePersistListFilters } from '@/modules/my-team/hooks/usePersistListFilters';
import { TeamActionTypes } from '@/store/team';
import { ChangeTacticAction, EnumDevice, PlayerPositionAbbr } from '@/types';
import {
  PlayerHistoric,
  PlayerClub,
  EnumTeamContext,
  TemplatePlayer,
  Player,
} from '@/types/team';
import {
  CODES_ERRORS,
  MAX_INPUT_RANGE,
  MIN_INPUT_RANGE,
  TACTICS_ARR,
  SLIDE_IN_OUT_LEFT,
  tacticToString,
  cn,
  lockScroll,
} from '@/utils';
import { ADS_DIV_ID, SECTION_NAME_FOR_ADS } from '@/utils/ads';

const initOptionsChange = {
  enablePosition: null,
  rowMessage: '',
  action: null,
  count: null,
};

export const Team11 = () => {
  const location = useLocation();
  const isScoreTab = location.state?.showScoreTab;
  const { tab, handleTabChanges } = useHandleChangesTabs(
    isScoreTab ? TabType.SecondTab : TabType.FirstTab
  );

  const [optionsChanges, setOptionsChanges] =
    useState<ChangeOptions>(initOptionsChange);
  const [suggestionSessionValue, setSuggestionSessionValue] = useState<boolean>(
    () => {
      const savedValue = sessionStorage.getItem('suggestionSessionValue');
      return savedValue === 'true' ? true : savedValue !== 'false';
    }
  );
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [shouldStartOnboarding, setShouldStartOnboarding] = useState(false);

  useMetaData({
    title: 'Mi Equipo de Primera',
  });
  const toast = useToast();
  const { isDesktop, isMobile } = useIsDesktop();
  const { userInitialData } = useAuth();
  useCheckPublicacion('A');
  const { state, dispatch } = useTeamStore();
  const { pinsData, handleSetPinsData } = usePinsAccount();

  const isBeforeVeda = useIsBeforeVeda(userInitialData?.comienzo_veda || '');
  const { freezeListPosition, getListFiltersToRestore } =
    usePersistListFilters();

  const { isReady } = useGoogleAds({
    adsSections: SECTION_NAME_FOR_ADS.miequipo,
  });

  const {
    data: getCurrentTeamData,
    isFetching: isFetchingCurrentTeam,
    refetch,
  } = useGetCurrentTeamF11();

  const {
    mutate: postDataRanking,
    data: responseRankingHistoric,
    isLoading: isLoadingRankingHistoric,
    error: errorRankingHistoric,
  } = usePostDataRanking();

  const { handleTransferSubmit, isLoadingTransfer, responseTransfer } =
    useHandleTransfer();

  const {
    handleNewCaptain,
    responseNewCaptain,
    isLoadingNewCaptain,
    updateCaptain,
  } = useHandleChangeCaptain();

  const { getPlayersOnField } = useTeam();
  const { resolveTeamPlayerData } = usePlayerClub();
  const { setTeamBalance } = useBalance();

  const [localStorageOnboarding] = useLocalStorage<Maybe<string>>(
    `onboarding-${EnumTeamContext.Team11}-${userInitialData?.idUsuarioDT}`,
    null
  );

  useEffect(() => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        isBeforeVeda,
      },
    });
  }, [dispatch, isBeforeVeda]);

  const changeSuggestionValue = () => {
    setSuggestionSessionValue((prevValue) => !prevValue);
  };

  const openSuggestionModal = () => setIsSuggestionOpen(true);
  const closeSuggestionModal = () => {
    setIsSuggestionOpen(false);
  };

  const isTransitioningRef = useRef(false);
  const pendingPlayerRef = useRef<TemplatePlayer | null>(null);

  // keepListFilters: false en montaje/desmontaje para inicializar los filtros de la seccion (DTA-10108)
  const handleResetActions = (keepListFilters = true) => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        responseTeamF11: undefined,
        selectedPlayerOnList: null,
        selectedPlayerOnTeam: null,
        showPlayersList: false,
        showFiltersPlayersList: false,
        showModalChangePlayer: false,
        playerDetailsModalOpen: false,
        showModalRecommender: false,
        addToTeamBySuggestionModal: false,
        ...((keepListFilters && getListFiltersToRestore()) || {
          selectedPosition: null,
          filtersApplied: {
            intoBudget: false,
            orderBy: null,
            orderClub: null,
            valuesRange: [MIN_INPUT_RANGE, MAX_INPUT_RANGE],
          },
        }),
      },
    });
    setOptionsChanges(initOptionsChange);
    if (state.tactic && state.team)
      handleSetTeamOnField(state.tactic?.id, state.team, false);
  };

  // fijo el scroll cuando abro filtros, lista de jugadores o listado de transferencia desde ayudante
  useEffect(() => {
    lockScroll(
      Boolean(
        isMobile &&
          (state.showFiltersPlayersList ||
            state.showPlayersList ||
            state.showPlayersListTransfer)
      )
    );

    return () => {
      lockScroll();
    };
  }, [
    state.showPlayersListTransfer,
    state.showFiltersPlayersList,
    state.showPlayersList,
    isMobile,
  ]);

  // hago scroll al top de pagina cuando abro modal de detalle de jugador
  useEffect(() => {
    if (
      state.selectedPlayerOnTeam &&
      !state.showPlayersListTransfer &&
      isDesktop &&
      !state.showPlayersList
    ) {
      scrollTo(0, 265);
    }
  }, [
    state.selectedPlayerOnTeam,
    state.showPlayersListTransfer,
    isDesktop,
    state.showPlayersList,
  ]);

  // Hago una nueva llamada al servicio de equipo actual para actualizar pines y refrescar data de equipo post transferencia
  useEffect(() => {
    if (
      responseTransfer?.estado ||
      pinsData.pinsAvailable !== userInitialData?.pinesDisponibles
    ) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseTransfer, pinsData.pinsAvailable]);

  // limpia state en acciones de back, close o post submit data. Dependiendo
  useEffect(() => {
    return () => {
      handleResetActions(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresco equipo luego del cambio
  const isTeamLoadedRef = useRef(false);
  useEffect(() => {
    handleResetActions(isTeamLoadedRef.current);
    isTeamLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.team]);

  // efecto maneja tabs
  useEffect(() => {
    if (tab === TabType.FirstTab) {
      if (getCurrentTeamData?.estado) {
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            responseTeamF11: updateCaptain.isUpdateCaptain
              ? {
                  ...getCurrentTeamData,
                  equipo: {
                    ...getCurrentTeamData.equipo,
                    jugadores: getCurrentTeamData.equipo.jugadores.map(
                      (jugador) =>
                        jugador.id === updateCaptain.id
                          ? { ...jugador, capitan: true }
                          : { ...jugador, capitan: false }
                    ),
                  },
                }
              : getCurrentTeamData,
          },
        });

        handleSetTeamOnField(
          getCurrentTeamData.equipo.idTactica,
          updateCaptain.isUpdateCaptain
            ? getCurrentTeamData.equipo.jugadores.map((jugador) =>
                jugador.id === updateCaptain.id
                  ? { ...jugador, capitan: true }
                  : { ...jugador, capitan: false }
              )
            : getCurrentTeamData.equipo.jugadores
        );

        setTeamBalance(
          parseInt(getCurrentTeamData.cotizacion_equipo.replaceAll('.', ''))
        );
      }
    } else if (tab === TabType.SecondTab && responseRankingHistoric) {
      const teamHistoric = responseRankingHistoric.jugadores.map(
        (p: PlayerHistoric) => {
          return {
            orden: p.orden,
            id: p.jugador,
            titular: p.titular,
            capitan: p.capitan,
            puntaje: p.puntaje,
          } as PlayerHistoric;
        }
      );

      const infoTeam = resolveTeamPlayerData(teamHistoric)?.map((player) =>
        'cotizacion' in player ? player?.cotizacion : 0
      );

      const balance =
        infoTeam &&
        infoTeam?.reduce((prev = 0, curr = 0) => {
          return prev + curr;
        });

      handleSetTeamOnField(responseRankingHistoric.idTactica, teamHistoric);
      balance && setTeamBalance(balance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, responseRankingHistoric, getCurrentTeamData, tab]);

  // manejo el error de ranking y voy al primer tab
  useEffect(() => {
    if (errorRankingHistoric) {
      handleTabChanges(TabType.FirstTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorRankingHistoric]);

  // MANEJO DE RESPUESTA DE TRANSFERENCIA
  useEffect(() => {
    if (
      responseTransfer?.estado &&
      'equipo' in responseTransfer &&
      responseTransfer?.equipo &&
      responseTransfer.cotizacion_equipo
    ) {
      const isNewCaptain =
        (getCurrentTeamData?.estado &&
          getCurrentTeamData.equipo.jugadores.find(
            (player) => player.id === state.selectedPlayerOnTeam?.id
          )?.capitan) ||
        false;

      const isNewTactic =
        state.tactic?.id !== responseTransfer.equipo.idTactica;

      // transferencia con exito
      toast.show(
        <>
          <strong>Transferencia realizada.</strong>{' '}
          {isNewCaptain
            ? `Tu nuevo capitán es ${
                state.selectedPlayerOnList?.a
              } ${state.selectedPlayerOnList?.n.at(0)}.`
            : `${
                state.selectedPlayerOnList?.a
              } ${state.selectedPlayerOnList?.n.at(
                0
              )} ahora forma parte de tu equipo. ${
                isNewTactic
                  ? `Tu nueva táctica es ` +
                    tacticToString(responseTransfer.equipo.idTactica, '-')
                  : ''
              }`}
        </>
      );

      // Redirijo a mi equipo: cierro la lista de jugadores y modal
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          showPlayersList: false,
          playerDetailsModalOpen: false,
          addToTeamBySuggestionModal: false,
          selectedPlayerOnList: null,
          selectedPlayerOnTeam: null,
          balance: parseInt(
            responseTransfer.cotizacion_equipo.replaceAll('.', '')
          ),
        },
      });
      // Refresco equipo en cancha
      handleSetTeamOnField(
        responseTransfer.equipo.idTactica,
        responseTransfer.equipo.jugadores
      );
    } else {
      if (!responseTransfer?.requiere_pin) {
        // error al querer transferir un equipo a una tactica inexistente
        const messageError =
          responseTransfer?.codError === CODES_ERRORS.MY_TEAM11.INVALID_TRANSFER
            ? 'No se puede realizar esta transferencia. La formación no se ajusta a ninguna de las 9 tácticas disponibles.'
            : responseTransfer?.mensaje;

        toast.show(messageError);
      } else {
        handleSetPinsData({
          ...pinsData,
          showModalWhatsPins: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseTransfer]);

  // MANEJO DE RESPUESTA DE NUEVO CAPITAN
  useEffect(() => {
    if (responseNewCaptain?.estado && responseNewCaptain.equipo) {
      const newCaptain = userInitialData?.plantilla.jugadores?.find(
        (player) => player.id === state.selectedPlayerOnTeam?.id
      );

      // cambio con exito
      toast.show(
        `Tu nuevo capitán es ${newCaptain?.a} ${newCaptain?.n.at(0)}.`
      );
      // Redirijo a mi equipo: cierro la lista de jugadores y modal
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          playerDetailsModalOpen: false,
          selectedPlayerOnTeam: null,
        },
      });
      // Refresco equipo en cancha
      handleSetTeamOnField(
        responseNewCaptain.equipo.idTactica,
        responseNewCaptain.equipo.jugadores
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseNewCaptain]);

  // MANEJO DE LOCAL STORAGE PARA ONBOARDING
  useEffect(() => {
    if (localStorageOnboarding) {
      const jsonStorage = JSON.parse(localStorageOnboarding);
      // valido si muestro el onboarding de mi equipo o armado de equipo
      if (userInitialData?.tiene_equipo) {
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            storageOnboardingTeam: jsonStorage?.onboardingTeam,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorageOnboarding, userInitialData?.tiene_equipo]);

  // SETEO LA VARIABLE SESSION STORAGE PARA CONTROLAR EL POPUP DE SUGERENCIAS
  useEffect(() => {
    sessionStorage.setItem(
      'suggestionSessionValue',
      suggestionSessionValue.toString()
    );
  }, [suggestionSessionValue]);

  // Resetea el modal de pines cuando cambio hacia atras en la vista
  useUndepsHostbackButton(() => {
    handleSetPinsData({
      ...pinsData,
      showInputsModalPins: false,
      showModalWhatsPins: false,
    });
  });

  const handleBackButton = useCallback(() => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        ...(!state.showFiltersPlayersList
          ? {
              showPlayersList: false,
            }
          : { showFiltersPlayersList: false }),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showFiltersPlayersList]);
  useHostbackButton(handleBackButton);

  const handleSelectPlayer = useCallback(
    (player: PlayerClub | TemplatePlayer, isChange?: boolean | undefined) => {
      if ('posicion' in player)
        freezeListPosition(player.posicion?.nombreCorto);
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          ...('posicion' in player
            ? {
                selectedPosition: player.posicion?.nombreCorto,
                selectedPlayerOnTeam: player as TemplatePlayer & PlayerClub,
              }
            : {
                selectedPlayerOnList: player as TemplatePlayer,
              }),
          ...(isChange
            ? { playerDetailsModalOpen: false, showModalChangePlayer: true }
            : { playerDetailsModalOpen: true }),
        },
      });
    },
    [dispatch, freezeListPosition]
  );

  // Esta funcion se encarga de dibujar los jugadores en la cancha
  const handleSetTeamOnField = useCallback(
    (
      idTactic: number,
      team: PlayerClub[] | PlayerHistoric[] | Player[],
      isChange?: boolean
    ) => {
      const players = getPlayersOnField({
        team,
        idTactic,
        handleSelectPlayer,
        context: isChange
          ? EnumTeamContext.Changes11
          : tab === TabType.FirstTab
          ? EnumTeamContext.Team11
          : EnumTeamContext.Score11,
      });

      const data = {
        type: TeamActionTypes.SET_STATE,
        payload: {
          team,
          tactic: { id: idTactic, formation: TACTICS_ARR[idTactic] },
          playersOnField: {
            goalkeepers: players.goalkeepers,
            defenders: players.defenders,
            midfielders: players.midfielders,
            forwards: players.forwards,
            substitutes: players.substitutes,
          },
        },
      };

      dispatch(data);

      return data.payload.playersOnField;
    },
    [dispatch, getPlayersOnField, handleSelectPlayer, tab]
  );

  useEffect(() => {
    const mustShow =
      getCurrentTeamData?.estado &&
      getCurrentTeamData.equipo.jugadores.length !== 0 &&
      state.storageOnboardingTeam !==
        `showed-${userInitialData?.idUsuarioDT}` &&
      isReady;
    const showOnboarding = setTimeout(() => {
      setShouldStartOnboarding(!!mustShow);
    }, 1000);

    return () => {
      clearTimeout(showOnboarding);
    };
  }, [state, userInitialData, isReady, getCurrentTeamData]);

  const tShirtCard = (
    <BoxTshirtEdit
      infoDt={{
        name: `${userInitialData?.nombre_dt}`,
        nameTeam: `${userInitialData?.nombre_equipo}`,
      }}
      tactic={{
        id: state.tactic?.id || 0,
        formation: state.tactic?.formation || [],
      }}
    />
  );

  const rankingCard = useCallback(() => {
    if (getCurrentTeamData?.estado) {
      const currentDateIsPublicated =
        getCurrentTeamData?.infoPuntajes.idFecha ===
        userInitialData?.id_ult_fecha_puntos;
      const dataCurrentMatchDay = {
        dateName: userInitialData?.fechaMostrarRankingF11.nombre,
        ...(currentDateIsPublicated && {
          matchDayScore: getCurrentTeamData?.infoPuntajes.puntosFecha,
        }),
        gralScore: getCurrentTeamData?.infoPuntajes.puntosGeneral,
        ...(currentDateIsPublicated && {
          matchDayPosition: getCurrentTeamData?.infoPuntajes.posicionFecha,
        }),
        gralPosition: getCurrentTeamData?.infoPuntajes.posicionGeneral,
      };
      return (
        <BoxRankingTeam
          context={
            tab === TabType.FirstTab
              ? EnumTeamContext.Team11
              : EnumTeamContext.Score11
          }
          isLoading={isLoadingRankingHistoric}
          lastRanking={{
            dateName: dataCurrentMatchDay.dateName,
            matchDayScore: dataCurrentMatchDay.matchDayScore,
            gralScore: dataCurrentMatchDay.gralScore,
            matchDayPosition: dataCurrentMatchDay.matchDayPosition,
            gralPosition: dataCurrentMatchDay.gralPosition,
          }}
          matchsDaysAvailables={userInitialData?.fechas_disponibles_obj || []}
          responseRanking={responseRankingHistoric || null}
          onSubmit={(date: string) => {
            postDataRanking(date);
          }}
        />
      );
    }
    // eslint-disable-next-line prettier/prettier, react-hooks/exhaustive-deps
  }, [
    getCurrentTeamData,
    tab,
    isLoadingRankingHistoric,
    responseRankingHistoric,
    userInitialData,
  ]);

  const contentStepsOnboarding: Step[] = [
    {
      target: '.refPins',
      title: 'PINES',
      content:
        'Los pines sirven para realizar transferencias. La primera transferencia de la fecha es gratuita.',
      placement: 'bottom',
      disableBeacon: true,
      disableScrolling: false,
    },
    {
      target: '#refPlayerField-1',
      title: 'JUGADORES',
      content:
        'Seleccionado un jugador podés cambiarlo por su suplente, transferirlo por un nuevo jugador o hacerlo capitán de tu equipo.',
      placement: 'bottom',
    },
  ];

  const alertInfoSuggestions = useMemo(() => {
    return (
      <Alert
        className="bg-gdt-blue max-w-[38rem] w-full lg:mt-5   "
        message={
          <div className="flex flex-wrap">
            {getCurrentTeamData?.estado &&
            getCurrentTeamData.arraySugerenciaTO.every(
              (player) => player.idGrupo === 1
            ) ? (
              <p>
                Tenés futbolistas que no jugarían la{' '}
                <span className="whitespace-nowrap me-1">
                  {userInitialData?.fechaActiva.nombre}.{' '}
                </span>
              </p>
            ) : (
              <span className="me-1">
                Hay futbolistas que no te vienen rindiendo.{' '}
              </span>
            )}
            <strong
              className="cursor-pointer flex-auto whitespace-nowrap"
              onClick={openSuggestionModal}
            >
              Ver Sugerencias
            </strong>
          </div>
        }
        variant={isMobile ? EnumDevice.Mobile : EnumDevice.Desktop}
        onCloseAction={changeSuggestionValue}
      />
    );
  }, [getCurrentTeamData, userInitialData?.fechaActiva.nombre, isMobile]);

  const showSuggestiveMessage = () => {
    return (
      getCurrentTeamData?.estado &&
      getCurrentTeamData.arraySugerenciaTO.length !== 0 &&
      suggestionSessionValue &&
      alertInfoSuggestions
    );
  };

  const tabs: DefaultTabsProps['tabs'] = useMemo(() => {
    return [
      { name: 'Equipo' },
      {
        name: 'Puntajes',
        disabled:
          userInitialData?.fechas_disponibles.length === 0 ||
          optionsChanges.action !== null,
      },
    ];
  }, [userInitialData, optionsChanges.action]);

  const handleChangePlayer = useCallback(
    (data: DetailsPlayerSubmit) => {
      const player = data.player;

      if (!state.team) return;

      const playerToBeChanged =
        state.team && state.team.find((team) => team.id === player.id);

      if (playerToBeChanged?.titular || player.p === 1) {
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            playerDetailsModalOpen: false,
            showModalChangePlayer: true,
          },
        });
      } else {
        // cambio opacidad de jugadores suplentes y titulares para seleccion de cambio
        if (state.selectedPosition && playerToBeChanged) {
          setOptionsChanges({
            enablePosition:
              PLAYERSFIELD_ID_KEYS_BY_ABBREVIATION[
                state.selectedPosition as keyof typeof PLAYERSFIELD_ID_KEYS_BY_ABBREVIATION
              ],
            rowMessage: 'ELEGÍ UN TITULAR',
            action: ChangeTacticAction.CHANGE,
            count: playerToBeChanged?.id || null,
          });
          dispatch({
            type: TeamActionTypes.SET_STATE,
            payload: {
              playerDetailsModalOpen: false,
            },
          });
          state.tactic &&
            state.team &&
            handleSetTeamOnField(state.tactic?.id, state.team, true);
        }
      }
    },
    [
      dispatch,
      handleSetTeamOnField,
      state.selectedPosition,
      state.tactic,
      state.team,
    ]
  );

  const handleCloseDetailsPlayerModal = useCallback(() => {
    if (isDesktop) {
      const isSelectedFromList = Boolean(state.selectedPlayerOnList?.id);
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          ...(isSelectedFromList
            ? {
                selectedPlayerOnList: null,
              }
            : {
                selectedPlayerOnTeam: null,
                ...getListFiltersToRestore(),
              }),
          playerDetailsModalOpen: false,
        },
      });
    } else {
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          playerDetailsModalOpen: false,
          ...(!state.showPlayersList && { selectedPlayerOnTeam: null }),
        },
      });
    }
  }, [
    dispatch,
    getListFiltersToRestore,
    isDesktop,
    state.selectedPlayerOnList?.id,
    state.showPlayersList,
  ]);

  const handleDetailsModalSubmit = useCallback(
    (data: DetailsPlayerSubmit) => {
      // https://agea.atlassian.net/browse/DTA-9775
      if (!state.selectedPlayerOnTeam) {
        toast.show('Primero elegí al jugador saliente');

        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            playerDetailsModalOpen: false,
          },
        });

        return;
      }

      if (data.willBeChanged) {
        handleChangePlayer(data);
        return;
      }
      if (data.willBeCaptain) {
        handleNewCaptain(data.player);
        return;
      }

      if (!state.showPlayersList) {
        // Guardar el player en el ref ANTES del dispatch
        pendingPlayerRef.current = data.player;

        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            selectedPlayerOnTeam: data.player,
            ...(isMobile
              ? {
                  selectedPlayerOnList: data.player,
                }
              : { showPlayersList: true }),
            playerDetailsModalOpen: false,
          },
        });

        if (!isTransitioningRef.current) {
          isTransitioningRef.current = true;
        }
      } else {
        handleTransferSubmit(data);
      }
    },
    [
      dispatch,
      handleChangePlayer,
      handleNewCaptain,
      handleTransferSubmit,
      isMobile,
      state.showPlayersList,
    ]
  );

  if (optionsChanges.enablePosition !== null && isMobile) {
    return (
      <>
        <ChangePlayer
          handleClose={() => handleResetActions()}
          positionSelected={state.selectedPosition as PlayerPositionAbbr}
        >
          <FieldLayout.Main>
            <FieldLayout.Inner>
              <FieldLayout.Field
                changeTacticOptions={optionsChanges}
                context={EnumTeamContext.Team11}
                defenders={state.playersOnField?.defenders}
                forwards={state.playersOnField?.forwards}
                goalkeeper={state.playersOnField?.goalkeepers}
                midfielders={state.playersOnField?.midfielders}
                selectedPositionOrden={
                  (state.selectedPlayerOnTeam &&
                    state.selectedPlayerOnTeam.id) ||
                  undefined
                }
                subtitutes={state.playersOnField?.substitutes}
                tacticId={state.tactic?.id ?? 1}
              />
            </FieldLayout.Inner>
          </FieldLayout.Main>
        </ChangePlayer>
        <ModalChangeConfirm />
      </>
    );
  }

  return (
    <>
      <Layout
        main={() => {
          return (
            <>
              <section className="px-4 lg:px-0 overflow-hidden">
                {shouldStartOnboarding && (
                  <OnboardingStepper
                    contentSteps={contentStepsOnboarding}
                    context={EnumTeamContext.Team11}
                    userId={userInitialData?.idUsuarioDT}
                    onClose={() => {
                      dispatch({
                        type: TeamActionTypes.SET_STATE,
                        payload: {
                          storageOnboardingTeam: `showed-${userInitialData?.idUsuarioDT}`,
                        },
                      });
                    }}
                  />
                )}
                {isMobile && (
                  <Tabs
                    activeTab={tab}
                    tabs={tabs}
                    onClick={(tab: TabType) => handleTabChanges(tab)}
                  />
                )}
                {isMobile && tab === TabType.SecondTab && rankingCard()}
                {((isMobile && tab === TabType.FirstTab) || isDesktop) && (
                  <div className="mt-4 lg:mt-0">
                    <HeaderMyTeam context={EnumTeamContext.Team11} />
                  </div>
                )}
                {isMobile && (
                  <div className="mt-4">{showSuggestiveMessage()}</div>
                )}
                <div className="relative mt-4">
                  {isDesktop && (
                    <div className="absolute w-full top-0 z-[2] flex items-center flex-col lg:top-5">
                      {
                        <Tabs
                          activeTab={tab}
                          tabs={tabs}
                          onClick={(tab: TabType) => handleTabChanges(tab)}
                        />
                      }
                      {isDesktop && showSuggestiveMessage()}
                    </div>
                  )}
                  {
                    <Loader
                      isVisible={
                        isFetchingCurrentTeam ||
                        isLoadingTransfer ||
                        isLoadingRankingHistoric ||
                        isLoadingNewCaptain
                      }
                      timeout={
                        isLoadingTransfer ||
                        isLoadingRankingHistoric ||
                        isLoadingNewCaptain
                      }
                    />
                  }
                  <FieldLayout.Main>
                    {/* Close btn backdrop */}
                    {(optionsChanges.action !== null && isDesktop) ||
                    (state.showPlayersList &&
                      isDesktop &&
                      !state.playerDetailsModalOpen) ? (
                      <button
                        className={cn(
                          'p-2 absolute right-[-15px] top-[-107.5px] z-[4] hidden lg:block bg-opacity-30 bg-white scale-[.4] rounded-full',
                          {
                            'right-0 top-0 bg-transparent scale-1':
                              optionsChanges.action !== null,
                          }
                        )}
                        type="button"
                        onClick={() => handleResetActions()}
                      >
                        <FiX className="w-[50px] h-[50px] text-white" />
                      </button>
                    ) : null}
                    <FieldLayout.Inner>
                      <FieldLayout.Field
                        backDropOnClick={() => {
                          handleResetActions();
                        }}
                        backDropOpen={Boolean(
                          state.selectedPlayerOnTeam &&
                            isDesktop &&
                            !state.playerDetailsModalOpen &&
                            !optionsChanges.action
                        )}
                        changeTacticOptions={optionsChanges}
                        context={EnumTeamContext.Team11}
                        defenders={state.playersOnField?.defenders}
                        forwards={state.playersOnField?.forwards}
                        goalkeeper={state.playersOnField?.goalkeepers}
                        isMounted={isDesktop}
                        midfielders={state.playersOnField?.midfielders}
                        selectedPositionOrden={
                          (state.selectedPlayerOnTeam &&
                            state.selectedPlayerOnTeam.id) ||
                          undefined
                        }
                        subtitutes={state.playersOnField?.substitutes}
                        tacticId={state.tactic?.id ?? 1}
                      />
                    </FieldLayout.Inner>
                  </FieldLayout.Main>
                </div>
                {/* {isMobile && tab === TabType.FirstTab && tShirtCard} */}
              </section>
              {/* // AYUDANTE DE CAMPO */}
              <div className="lg:mb-11">
                <FieldAssistant
                  context={EnumTeamContext.Team11}
                  handleTransferAssistante={handleTransferSubmit}
                />
              </div>
            </>
          );
        }}
        sidebar={() => {
          return (
            <>
              {(state.showPlayersList || state.isBeforeVeda) &&
                tab === TabType.FirstTab &&
                isDesktop && (
                  <motion.div
                    animate={'show'}
                    className="min-w-[360px] sticky top-0 z-[1] flex flex-col lg:h-screen bg-white"
                    initial="hide"
                    variants={SLIDE_IN_OUT_LEFT}
                  >
                    <DesktopSidebar
                      context={EnumTeamContext.FilterTeam11}
                      handleSelectPlayerOnList={handleSelectPlayer}
                      onClick={() => {
                        dispatch({
                          type: TeamActionTypes.SET_STATE,
                          payload: {
                            showFiltersPlayersList:
                              !state.showFiltersPlayersList,
                          },
                        });
                      }}
                    />
                  </motion.div>
                )}
              <section className="space-y-4">
                {isDesktop && (
                  <Fragment>
                    {tShirtCard}
                    {rankingCard()}
                    <AdSlot
                      className="mx-auto"
                      divId={ADS_DIV_ID.caja1}
                      variant="BOX"
                    />
                    {getCurrentTeamData?.estado &&
                      getCurrentTeamData.novedadDestacada?.descripcion && (
                        <BoxNewsTeam
                          description={
                            getCurrentTeamData.novedadDestacada.descripcion
                          }
                        />
                      )}
                    <AdSlot
                      className="mx-auto"
                      divId={ADS_DIV_ID.caja2}
                      variant="BOX"
                    />
                    <BoxGoPremium
                      className="pb-7"
                      context={EnumTeamContext.Team11}
                      showStar={false}
                    />
                    <AdSlot
                      className="mx-auto"
                      divId={ADS_DIV_ID.caja3}
                      variant="BOX"
                    />
                  </Fragment>
                )}
              </section>
            </>
          );
        }}
        title={`${userInitialData?.nombre_equipo}`}
        trackSection={SECTION_NAME_FOR_ADS.miequipo}
      />
      {/* Modal para detalle del jugador */}
      <Modal
        bottomSheetClassName="px-[16px]"
        className="px-[1.06rem] lg:px-11 py-10"
        closeOutside={state.showPlayersList}
        controlledOnClose={handleCloseDetailsPlayerModal}
        isControlledOpen={Boolean(
          state.playerDetailsModalOpen && !state.showPlayersListTransfer
        )}
        render={() => {
          if (
            state.selectedPlayerOnTeam?.id ||
            state.selectedPlayerOnList?.id
          ) {
            return (
              <DetailsPlayer
                messageBeforeActions={
                  state.selectedPlayerOnList &&
                  getCurrentTeamData?.estado &&
                  getCurrentTeamData.seLeDescontara1Pin && (
                    <PinsDiscountLegend />
                  )
                }
                playerId={
                  !state.showPlayersList && state.selectedPlayerOnTeam
                    ? state.selectedPlayerOnTeam?.id
                    : state.selectedPlayerOnList?.id
                }
                playersOnField={state.playersOnField}
                selectedPlayerOnField={state.selectedPlayerOnTeam}
                selectedPlayerOnList={state.selectedPlayerOnList}
                variant={
                  state.showPlayersList ||
                  (!state.selectedPlayerOnTeam && state.isBeforeVeda)
                    ? 'add'
                    : 'transfer'
                }
                onSubmitPlayer={handleDetailsModalSubmit}
              />
            );
          }

          return null;
        }}
        size="xs"
        variant="controlled"
        onAfterClose={() => {
          isTransitioningRef.current = false;

          if (pendingPlayerRef.current) {
            dispatch({
              type: TeamActionTypes.SET_STATE,
              payload: { showPlayersList: true, playerDetailsModalOpen: false },
            });
            pendingPlayerRef.current = null;
          }
        }}
      />

      {/* Modal para cambiar jugador sugerido */}
      <Modal
        bottomSheetClassName="px-[16px]"
        className="px-[1.06rem] lg:px-11 py-10"
        controlledOnClose={() => {
          dispatch({
            type: TeamActionTypes.SET_STATE,
            payload: {
              addToTeamBySuggestionModal: false,
              selectedPlayerOnList: null,
              selectedPlayerOnTeam: null,
            },
          });
        }}
        isControlledOpen={Boolean(state.addToTeamBySuggestionModal)}
        render={() => {
          if (state.selectedPlayerOnList?.id) {
            return (
              <DetailsPlayer
                messageBeforeActions={
                  getCurrentTeamData?.estado &&
                  getCurrentTeamData?.seLeDescontara1Pin &&
                  state.selectedPlayerOnList && <PinsDiscountLegend />
                }
                playerId={state.selectedPlayerOnList?.id}
                selectedPlayerOnField={state.selectedPlayerOnTeam}
                selectedPlayerOnList={state.selectedPlayerOnList}
                variant={'add'}
                onSubmitPlayer={(data) => {
                  handleTransferSubmit(data);
                }}
              />
            );
          }
        }}
        size="xs"
        variant="controlled"
      />

      {/* SlideOver para seleccion de jugadores */}

      <AnimatePresence>
        {state.showPlayersList && isMobile && (
          <motion.nav
            animate="show"
            className="fixed top-0 w-full z-50"
            initial="hide"
            variants={SLIDE_IN_OUT_LEFT}
          >
            <MobileSidebar
              backAction={() => {
                dispatch({
                  type: TeamActionTypes.SET_STATE,
                  payload: {
                    selectedPlayerOnList: null,
                    selectedPlayerOnTeam: null,
                    selectedPosition: null,
                    showPlayersList: false,
                    filtersApplied: {
                      intoBudget: false,
                      orderBy: null,
                      orderClub: null,
                      valuesRange: [MIN_INPUT_RANGE, MAX_INPUT_RANGE],
                    },
                  },
                });
              }}
              context={EnumTeamContext.FilterTeam11}
              handleSelectPlayerOnList={handleSelectPlayer}
              title={`Transferir a ${state.selectedPlayerOnTeam?.a}`}
              onClick={() => {
                dispatch({
                  type: TeamActionTypes.SET_STATE,
                  payload: {
                    showFiltersPlayersList: true,
                  },
                });
              }}
            />
          </motion.nav>
        )}
      </AnimatePresence>
      {/* SlideOver para filtros */}
      <AnimatePresence>
        {state.showFiltersPlayersList && isMobile && (
          <motion.nav
            animate="show"
            className="fixed top-0 w-full z-50"
            initial="hide"
            variants={SLIDE_IN_OUT_LEFT}
          >
            <NavBar
              mobile={{
                rightContent: {
                  customRightAction: () => {
                    dispatch({
                      type: TeamActionTypes.SET_STATE,
                      payload: {
                        showFiltersPlayersList: false,
                        ...state.filtersApplied,
                      },
                    });
                  },
                },
                centerContent: {
                  title: `Filtros`,
                },
              }}
            >
              <FormFilterPlayers context={EnumTeamContext.Team11} />
            </NavBar>
          </motion.nav>
        )}
      </AnimatePresence>
      {/* modal  info pines */}

      <ModalWhatsPins
        showModal={
          (pinsData.showModalWhatsPins || pinsData.showInputsModalPins) &&
          Boolean(!state.showPlayersListTransfer)
        }
        onSubmitTransfer={handleTransferSubmit}
      />
      {/* modal confirmación de cambio */}
      <ModalChangeConfirm />
      {/* Modal para sugerir cambio de jugadores */}
      {isSuggestionOpen && (
        <ModalChangeSuggestion
          isOpen={isSuggestionOpen}
          onClose={closeSuggestionModal}
        />
      )}
    </>
  );
};
